import React, { useState, useEffect } from 'react';
import tumorImage from './images/projects_page_1/tumor_immunology.jpg';
import ProjectEntry from './ProjectEntry';
import project1Image from './images/projects_page_1/TIM3.jpg';
import project2Image from './images/projects_page_1/CART.jpg';
import project3Image from './images/projects_page_1/MHC.jpg';
import img1 from './images/intro_images/img1.jpg';
import img2 from './images/intro_images/img2.jpg';
import img3 from './images/intro_images/img3.jpg';
import img4 from './images/intro_images/img4.jpg';
import img5 from './images/intro_images/img5.jpg';
import img6 from './images/intro_images/img6.jpg';
import img7 from './images/intro_images/img7.jpg';
import img8 from './images/intro_images/img8.jpg';
import img9 from './images/intro_images/img9.jpg';
import img10 from './images/intro_images/img10.jpg';
import img11 from './images/intro_images/img11.jpg';
import img12 from './images/intro_images/img12.jpg';

const allImages = [img1, img2, img3, img4, img5, img6, img7, img8, img9, img10, img11, img12];

const Home = () => {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setOffset(prev => (prev + 1) % allImages.length);
    }, 120000);
    return () => clearInterval(interval);
  }, []);

  const gridImages = Array.from({ length: 8 }, (_, i) => allImages[(i + offset) % allImages.length]);

  return (
    <div>
      <div className="home-container">
        <div className="about-me">
          <h1>Hi, I'm Aonkon Dey</h1>
          <p className="pronunciation">[pronounced On-con]</p>
          <div className="bio-details">
            <p>
              Immunology PhD student at <span className="inst">Mount Sinai</span><br />
              <em>prev.</em> Computational Biologist at <span className="inst">Villani Lab @ MGH</span> and the <span className="inst">Broad Institute of Harvard and MIT</span><br />
              <em>prev.</em> Biomedical Engineering + CS at <span className="inst">Tufts</span>
            </p>
            <p>
              My research focuses on applying computational biology to advance cancer immunotherapy — spanning <span className="method">immune checkpoint blockades</span>, <span className="method">adoptive cell therapies</span>, and the discovery of novel therapeutic targets.
            </p>
            <p>
              With a background in biomedical engineering, computer science, and mathematics, I leverage large-scale <span className="method">single-cell and spatial genomics</span>, <span className="method">histopathology imaging</span>, and <span className="method">integrative multi-modal data analysis</span> to discover novel targets and optimize therapeutic design. I build and mine disease atlases to uncover regulatory programs underlying treatment response and relapse. I also benchmark and fine-tune <span className="method">single-cell and spatial foundation models</span>, and apply causal inference frameworks to disentangle regulatory programs underlying disease states and model perturbation responses.
            </p>
            <p>Contact: aonkon.dey@icahn.mssm.edu · dey@broadinstitute.org</p>
            <div className="social-links">
              <a
                href="https://www.researchgate.net/profile/Aonkon-Dey"
                target="_blank"
                rel="noopener noreferrer"
              >
                ResearchGate
              </a>
              <a
                href="https://www.linkedin.com/in/aonkon-dey/"
                target="_blank"
                rel="noopener noreferrer"
              >
                LinkedIn
              </a>
            </div>
          </div>
        </div>
        <div className="photo-grid-container">
          <div className="photo-grid">
            {gridImages.map((img, i) => (
              <img key={i} src={img} alt="" />
            ))}
          </div>
          <p className="camera-roll-text">Camera Roll</p>
        </div>
      </div>
      <hr className="section-divider" />
      <div className="research">
        <p className="bio-details">Some ways tumors evade your immune surveillance.</p>
        <div className="research-image-container">
          <img src={tumorImage} alt="Tumor Immunology" />
        </div>
      </div>
      <hr className="section-divider" />
      <div className="projects-section">
        <p className="bio-details">Featured projects from my time in the Villani lab, on cancer immunotherapy strategies designed to overcome these challenges.</p>
        <div className="projects-grid">
          <ProjectEntry
            image={project1Image}
            title="Single cell multiomics based retrospective study of anti-TIM3 immune checkpoint blockade (ICB) therapy combined with Azacitidine clinical trial for myeloid malignancies."
            description={
              <div>Investigated the immune landscape of high-risk Myelodysplastic Syndromes (MDS) and Chronic Myelomonocytic Leukemia (CMML) before and after <span className="method">anti-TIM3 immune checkpoint blockade (ICB)</span> therapy, from one of the first cohorts reported to respond to anti-TIM3. This retrospective study analyzed 450,000 profiles from bone marrow and peripheral blood, generated by <span className="method">scRNAseq</span> and <span className="method">CITE-seq</span>, which were integrated with another 460,000 bone marrow and blood cells of healthy donors, to comprehensively characterize 120 immune and hematopoetic stem cell subsets. We identified immune cell subsets and gene expression programs associated with MDS and CMML at baseline, and response to anti-TIM3+Aza therapy. Using baseline samples, we uncovered pro-inflammatory T-cell subsets and MHC-II expression in progenitors and myeloid cell subsets as key hallmarks of treatment response. Additionally, we identified gene expression shifts related to interferon response, NFKB inhibition and transcription factors in post-therapy samples in responding patients.
                <br /><br />
                <strong>Role:</strong> Primary author, computational lead
              </div>
            }
            links={[
              { text: "View Abstracts/Posters", url: "https://doi.org/10.1182/blood-2023-189858" },
              { text: "View Paper: [Pending submission]", url: "" },
              { text: "GitHub Repository: [Publicly available after publication]", url: "" }
            ]}
          />
          <ProjectEntry
            image={project2Image}
            title="Omics based identification of novel CAR T therapy antigen candidates for treating anaplastic thyroid carcinoma."
            description={
              <div>Developed an in-silico antigen target identification pipeline to nominate tumor-associated antigens (TAAs) for <span className="method">CAR T therapy</span> in thyroid carcinomas by leveraging publicly available <span className="method">scRNA-seq</span> and surface protein datasets. The pipeline nominates cell surface coding genes that are minimally expressed across healthy tissues but broadly and specifically expressed in malignant cells. Using papillary thyroid carcinoma as a positive control, the pipeline successfully identified known TAAs. For anaplastic thyroid carcinoma, a rare and aggressive cancer, the pipeline nominated multiple TAA candidates. At the Jan Lab at MGH, two of these targets were validated for protein expression in tissue, and one successfully validated in-vitro and in-vivo for clearance of dedifferentiated thyroid cancer cells.
                <br /><br />
                <strong>Co-mentored:</strong> Max Jan<br />
                <strong>Role:</strong> Co-first author, computational lead
              </div>
            }
            links={[
              { text: "View Abstracts/Posters", url: "https://meetings.cshl.edu/abstracts.aspx?meet=DATA&year=24" },
              { text: "View Paper: [Pending submission]", url: "" },
              { text: "GitHub Repository: [Publicly available after publication]", url: "" }
            ]}
          />
          <ProjectEntry
            image={project3Image}
            title="[Collaboration] Class I HLA-independent lysis of cancer cells by tumor-infiltrating CD8 T cells"
            description={
              <div>Characterized the transcriptional state and clonal dynamics of a novel class of tumouricidal CD8+ tumor infiltrating lymphocytes (TILs) capable of class I HLA-independent cancer cell lysis, using <span className="method">scRNA-seq</span> analytical strategies. Performed <span className="method">cellular communication analysis</span> to nominate possible receptor-ligand interactions between TILs and matched melanoma tumor cells. Built an atlas of expanded TILs and baseline tumor digests of melanoma from publicly available single-cell datasets, to identify transcriptional phenotypes and clonal dynamics enriched in expanded TILs compared to baseline. Identified upregulated gene programs in OT1 transgenic mice CD8+ T cells that acquired Class I MHC independent cancer cell killing over time. Coordinated with biopharmaceutical collaborators to derive relevant insights from their <span className="method">TIL therapy</span> cohorts.
                <br /><br />
                <strong>Collaboration:</strong> Hongyan Xie (Primary author), Russell Jenkins (PI)<br />
                <strong>Role:</strong> Co-second author, computational lead (scRNA-seq)
              </div>
            }
            links={[
              { text: "View Abstracts/Posters", url: "https://aacrjournals.org/cancerres/article/84/6_Supplement/3968/740653" },
              { text: "View Paper: [In Review]", url: "" },
              { text: "GitHub Repository: [Publicly available after publication]", url: "" }
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default Home;
